// Performance V2 — Ads tab (#4185). Sub-views: Ads (per-ad table with Δ%
// cells), Headlines (#2820/#2821 lifetime aggregates), Landing Pages (#4308
// per-final_url rollup). Extracted from performance-v2.tsx (#4932 Phase 0.3);
// no behavior change. adMetrics is computed in the shell (the header toolbar's
// ad filter and the Overview top-performers widget share it) and arrives as a
// prop, as does the #4293 previous-period map; the headline fan-out queries
// live here (the module mounts only when the tab is active).
import { Spinner } from '@/components/glitch-ui/spinner';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import { BarChart3, Globe } from 'lucide-react';
import { ColumnDef, CellContext } from '@tanstack/react-table';

import type { AdPerformanceData } from '@/lib/apis';
import type { CampaignData } from '@/lib/types';
import { campaignHeadlineMetricsQueryOptions, useLandingPageExperienceQuery } from '@/lib/query-options';
import { DataTable } from '@/components/glitch-ui/data-table';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import type { ExportColumn } from '@/lib/csv-export';
import { cn, getFunnelStageLabel } from '@/lib/utils';
import { fmt, DeltaCell, PerformanceCell, CampaignStatePill, EmptyTabState, ExportCsvButton } from '../cells';

export type AdsSubView = 'ads' | 'headlines' | 'landing-pages';

// Shape of the shell's adMetrics memo rows: the raw ad-performance report row
// enriched with Glitch campaign context for display and linking.
export type AdMetric = AdPerformanceData & {
  campaignDisplayName: string;
  adDisplayName: string;
  campaignSlug: string | undefined;
  glitchAdId: number | undefined;
  /** Parent campaign's Glitch lifecycle state (Google Ads has no ad-level state) — the State column. */
  campaignState: string | null;
  /** True when the parent campaign isn't Glitch-managed (no Glitch record, or origin='external' — #6128). */
  isExternal: boolean;
  typeLabel: string;
  typeColor: string;
  conversions: number;
  cost: number;
  cpa: number;
  roas: number;
};

// Previous-period per-ad metrics keyed by Google Ads ad_id (#4293).
// Drives Δ% in the Ads tab. Same shape as adMetrics minus the derived
// display fields — only the numeric values the cells need.
export type PreviousAdTotals = {
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  cost: number;
  cpa: number;
};

export function AdsTab({
  subView,
  adMetrics,
  previousAdMetricsByAdId,
  activeCampaigns,
  visibleColumns,
  currencySymbol,
  dateRangeLabel,
  dateRangeFilter,
  layout,
  onReorder,
  onResize,
  renderChart,
}: {
  subView: AdsSubView;
  adMetrics: AdMetric[];
  previousAdMetricsByAdId: Map<string, PreviousAdTotals>;
  activeCampaigns: CampaignData[];
  visibleColumns: Set<string>;
  currencySymbol: string;
  dateRangeLabel: string;
  dateRangeFilter: { start_date: string; end_date: string };
  layout: WidgetConfig[];
  onReorder: (layout: WidgetConfig[]) => void;
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void;
  renderChart: () => React.ReactNode;
}) {
  const cs = currencySymbol;

  const [creativeFilters] = useState<Record<string, string[]>>({});

  // Filtered ad metrics for Creative tab
  const creativeAdMetrics = useMemo(() => {
    let filtered = adMetrics;
    const campaigns = creativeFilters.campaigns || [];
    const types = creativeFilters.types || [];
    const funnels = creativeFilters.funnels || [];
    if (campaigns.length > 0) {
      filtered = filtered.filter(ad => campaigns.includes(ad.campaignDisplayName));
    }
    if (types.length > 0) {
      filtered = filtered.filter(ad => types.includes(ad.typeLabel));
    }
    if (funnels.length > 0) {
      filtered = filtered.filter(ad => funnels.includes(ad.funnel_stage || ''));
    }
    return filtered;
  }, [adMetrics, creativeFilters]);

  // Headline metrics for the Ads > Headlines sub-view.
  // The backend headline GAQL is point-in-time (no date segmentation — see #2820/#2821),
  // so we fan out across active campaigns and aggregate by headline text. Lifetime totals
  // are intentional: the Google Ads API does not segment ad_group_ad_asset_view by date.
  const headlinesSubViewActive = subView === 'headlines';
  const headlineMetricsQueries = useQueries({
    queries: activeCampaigns.map(c => ({
      ...campaignHeadlineMetricsQueryOptions(c.id),
      enabled: headlinesSubViewActive,
    })),
  });

  // #5997 — landing-page experience (post_click_quality) per ad group, rolled up
  // to each URL in the Landing Pages sub-view. Point-in-time + account-wide, so
  // only fetched when that sub-view is active.
  const landingPagesSubViewActive = subView === 'landing-pages';
  const { data: landingPageExperienceRows } = useLandingPageExperienceQuery(undefined, {
    enabled: landingPagesSubViewActive,
  });
  const landingPageExperienceByAdGroup = useMemo(
    () => new Map((landingPageExperienceRows ?? []).map((r) => [r.ad_group_id, r] as const)),
    [landingPageExperienceRows],
  );

  type HeadlineMetric = {
    text: string;
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
    ctr: number;
    cpa: number;
    adCount: number;
    funnelStages: string[];
    performanceLabels: string[];
  };

  const headlineMetrics = useMemo<HeadlineMetric[]>(() => {
    if (!headlinesSubViewActive) return [];
    type Bucket = {
      text: string;
      impressions: number;
      clicks: number;
      conversions: number;
      cost: number;
      adCount: number;
      funnelStages: Set<string>;
      performanceLabels: Set<string>;
    };
    const byText = new Map<string, Bucket>();
    for (const q of headlineMetricsQueries) {
      const headlines = q.data?.headlines ?? [];
      for (const h of headlines) {
        const bucket = byText.get(h.headline_text) ?? {
          text: h.headline_text,
          impressions: 0, clicks: 0, conversions: 0, cost: 0, adCount: 0,
          funnelStages: new Set<string>(),
          performanceLabels: new Set<string>(),
        };
        bucket.impressions += h.impressions;
        bucket.clicks += h.clicks;
        bucket.conversions += h.conversions;
        bucket.cost += h.cost_dollars;
        bucket.adCount += 1;
        if (h.funnel_stage) bucket.funnelStages.add(h.funnel_stage);
        if (h.performance_label) bucket.performanceLabels.add(h.performance_label);
        byText.set(h.headline_text, bucket);
      }
    }
    return Array.from(byText.values())
      .map(b => ({
        text: b.text,
        impressions: b.impressions,
        clicks: b.clicks,
        conversions: Math.round(b.conversions),
        cost: Math.round(b.cost * 100) / 100,
        ctr: b.impressions > 0 ? (b.clicks / b.impressions) * 100 : 0,
        cpa: b.conversions > 0 ? Math.round((b.cost / b.conversions) * 100) / 100 : 0,
        adCount: b.adCount,
        funnelStages: Array.from(b.funnelStages),
        performanceLabels: Array.from(b.performanceLabels),
      }))
      .sort((a, b) => b.impressions - a.impressions);
  }, [headlineMetricsQueries, headlinesSubViewActive]);

  const headlineMetricsLoading = headlinesSubViewActive
    && headlineMetricsQueries.some(q => q.isLoading)
    && headlineMetrics.length === 0;

  const creativeColumns: ColumnDef<AdMetric, unknown>[] = [
    { accessorKey: 'ad_name', header: 'Ad', cell: ({ row }) => {
      const { adDisplayName, campaignSlug, glitchAdId, campaignDisplayName } = row.original;
      const isClickable = campaignSlug && glitchAdId;
      const funnel = row.original.funnel_stage;
      const funnelLabel = funnel ? getFunnelStageLabel(funnel) : null;
      return (
        <div>
          <div className="flex items-center gap-2">
            {isClickable ? (
              <Link to={`/campaigns/google/${campaignSlug}/ad/${glitchAdId}` as string} className="text-sm font-medium text-[#290BD9] hover:text-[#2309C5] transition-colors">
                {adDisplayName}
              </Link>
            ) : (
              <span className="text-sm font-medium text-gray-900">{adDisplayName}</span>
            )}
            {funnelLabel && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">{funnelLabel}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-xs text-gray-500">{campaignDisplayName}</span>
            <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium', row.original.typeColor)}>{row.original.typeLabel}</span>
          </div>
        </div>
      );
    }},
    // Parent campaign's lifecycle state (Live / Draft / Paused / …) — same column as Campaigns.
    { id: 'state', header: 'State', cell: ({ row }) => <CampaignStatePill state={row.original.campaignState} /> },
    { id: 'headlines', header: 'Headlines', cell: ({ row }) => {
      const headlines = row.original.headlines || [];
      if (headlines.length === 0) return <span className="text-xs text-gray-400">-</span>;
      return (
        <div className="max-w-[200px]">
          <p className="text-xs text-gray-900 truncate">{headlines[0]}</p>
          {headlines.length > 1 && (
            <p className="text-[10px] text-gray-400">+{headlines.length - 1} more</p>
          )}
        </div>
      );
    }},
    { id: 'performance', header: 'Performance', enableSorting: false, cell: ({ row }) => (
      <PerformanceCell
        impressions={row.original.impressions}
        clicks={row.original.clicks}
        conversions={row.original.conversions}
        cost={row.original.cost}
        currency={cs}
      />
    )},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => (
      <DeltaCell
        current={row.original.impressions}
        previous={previousAdMetricsByAdId.get(row.original.ad_id)?.impressions}
        format={(n) => n.toLocaleString()}
      />
    )},
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => (
      <DeltaCell
        current={row.original.clicks}
        previous={previousAdMetricsByAdId.get(row.original.ad_id)?.clicks}
        format={(n) => n.toLocaleString()}
      />
    )},
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => (
      <DeltaCell
        current={row.original.conversions}
        previous={previousAdMetricsByAdId.get(row.original.ad_id)?.conversions}
        format={(n) => n.toFixed(0)}
      />
    )},
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => (
      <DeltaCell
        current={row.original.ctr}
        previous={previousAdMetricsByAdId.get(row.original.ad_id)?.ctr}
        format={(n) => `${n.toFixed(2)}%`}
      />
    )},
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => (
      <DeltaCell
        current={row.original.cost}
        previous={previousAdMetricsByAdId.get(row.original.ad_id)?.cost}
        format={(n) => fmt(n, cs)}
        direction="neutral"
      />
    )},
    { accessorKey: 'cpa', header: 'CPA', cell: ({ row }) => (
      <DeltaCell
        current={row.original.cpa}
        previous={previousAdMetricsByAdId.get(row.original.ad_id)?.cpa}
        format={(n) => fmt(n, cs)}
        direction="lower-better"
      />
    )},
  ];

  const headlineColumns: ColumnDef<HeadlineMetric, unknown>[] = [
    { accessorKey: 'text', header: 'Headline', cell: ({ row }) => (
      <div>
        <p className="text-sm font-medium text-gray-900">{row.original.text}</p>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span className="text-xs text-gray-500">{row.original.adCount} {row.original.adCount === 1 ? 'ad' : 'ads'}</span>
          {row.original.performanceLabels.map(label => (
            <span key={label} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
              {label.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())}
            </span>
          ))}
          {row.original.funnelStages.map(stage => (
            <span key={stage} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#f3f1ff] text-[#290BD9]">
              {getFunnelStageLabel(stage)}
            </span>
          ))}
        </div>
      </div>
    )},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${row.original.ctr.toFixed(2)}%` },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
    { accessorKey: 'cpa', header: 'CPA', cell: ({ row }) => fmt(row.original.cpa, cs) },
  ];

  return (
    <WidgetGrid layout={layout} onReorder={onReorder} onResize={onResize}>
      {(widgetId, _widgetWidth) => {
        switch (widgetId) {
          // #4185 — Performance Over Time chart at the top of the Ads tab.
          // Reuses the Overview chart so users get ad-trend context next
          // to the per-ad table without leaving the tab. Account-level
          // time-series (ad-level daily data would need new backend
          // segmentation; that's a future enhancement).
          case 'performance-chart':
            return renderChart();

          case 'ads-content': {
            if (subView === 'ads') {
              // #4310 — Filter creativeColumns by user-chosen visibility.
              // External campaigns' ads aren't Glitch-managed, so every performance column (the
              // metric cells AND the "Performance" summary cell) reads "NA" rather than Google
              // numbers Glitch didn't drive. Allowlist the identity/content columns to KEEP (ad
              // name, state, headlines); everything else is blanked — robust as columns are added.
              const KEEP_FOR_EXTERNAL = new Set(['ad_name', 'state', 'headlines']);
              const visibleCreativeColumnDefs = creativeColumns
                .filter(c => {
                  const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
                  return id ? visibleColumns.has(id) : true;
                })
                .map(c => {
                  const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
                  if (!id || KEEP_FOR_EXTERNAL.has(id) || typeof c.cell !== 'function') return c;
                  const orig = c.cell;
                  return {
                    ...c,
                    cell: (ctx: CellContext<AdMetric, unknown>) =>
                      ctx.row.original.isExternal ? <span className="text-xs text-gray-400">NA</span> : orig(ctx),
                  };
                });
              // #4302 — CSV export for the Ads tab, matching the Google Ads UI Ad report shape.
              const adsExportColumns: ExportColumn<AdMetric>[] = [
                { header: 'Ad', value: r => r.adDisplayName ?? r.ad_name ?? '' },
                { header: 'Campaign', value: r => r.campaignDisplayName ?? r.campaign_name ?? '' },
                { header: 'Headlines', value: r => (r.headlines ?? []).join(' | ') },
                { header: 'Descriptions', value: r => (r.descriptions ?? []).join(' | ') },
                { header: 'Impressions', value: r => r.impressions },
                { header: 'Clicks', value: r => r.clicks },
                { header: 'CTR', value: r => `${r.ctr.toFixed(2)}%` },
                { header: 'Conversions', value: r => r.conversions },
                { header: 'Cost', value: r => r.cost },
                { header: 'CPA', value: r => r.cpa },
              ];
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-end pt-1 pr-10">
                    <ExportCsvButton
                      rows={creativeAdMetrics}
                      columns={adsExportColumns}
                      title="Ad report"
                      dateRange={dateRangeLabel}
                      startDate={dateRangeFilter.start_date}
                      endDate={dateRangeFilter.end_date}
                      filenameSlug="ad-report"
                    />
                  </div>
                  <DataTable<AdMetric> data={creativeAdMetrics} columns={visibleCreativeColumnDefs} pageSize={15} variant="bordered" emptyState="No ad performance data available for this period" />
                </div>
              );
            }
            if (subView === 'headlines') {
              if (headlineMetricsLoading) {
                return (
                  <div className="flex items-center justify-center py-16">
                    <Spinner size="lg" className="text-gray-400" />
                    <span className="ml-3 text-sm text-gray-500">Loading headline performance…</span>
                  </div>
                );
              }
              if (headlineMetrics.length === 0) {
                return (
                  <EmptyTabState
                    icon={BarChart3}
                    title="No Headline Data"
                    description="Headlines with traffic will appear here once campaigns generate impressions."
                  />
                );
              }
              return (
                <div className="space-y-2">
                  <DataTable<HeadlineMetric> data={headlineMetrics} columns={headlineColumns} pageSize={15} variant="bordered" emptyState="No headline performance data available" />
                  <p className="text-[11px] text-gray-400 px-1">
                    Lifetime totals — the Google Ads API does not segment headline metrics by date, so the date-range filter does not apply here.
                  </p>
                </div>
              );
            }
            // #4308 — Landing Pages view. Aggregates the per-ad metrics
            // by `final_url` so each row is one URL with rolled-up
            // performance. Skips ads without a URL (Display/Video).
            type LandingPageRow = {
              url: string;
              campaigns: Set<string>;
              adGroupIds: Set<string>;
              adCount: number;
              impressions: number;
              clicks: number;
              conversions: number;
              cost: number;
              ctr: number;
              cpa: number;
            };
            const byUrl = new Map<string, LandingPageRow>();
            for (const ad of creativeAdMetrics) {
              const url = ad.final_url;
              if (!url) continue;
              const existing = byUrl.get(url) ?? {
                url,
                campaigns: new Set<string>(),
                adGroupIds: new Set<string>(),
                adCount: 0,
                impressions: 0, clicks: 0, conversions: 0, cost: 0,
                ctr: 0, cpa: 0,
              };
              existing.impressions += ad.impressions;
              existing.clicks += ad.clicks;
              existing.conversions += ad.conversions;
              existing.cost += ad.cost;
              existing.adCount += 1;
              if (ad.campaignDisplayName) existing.campaigns.add(ad.campaignDisplayName);
              if (ad.ad_group_id) existing.adGroupIds.add(ad.ad_group_id);
              byUrl.set(url, existing);
            }
            const landingPageMetrics = Array.from(byUrl.values())
              .map(r => ({
                ...r,
                ctr: r.impressions > 0 ? (r.clicks / r.impressions) * 100 : 0,
                cpa: r.conversions > 0 ? r.cost / r.conversions : 0,
              }))
              .sort((a, b) => b.impressions - a.impressions);

            if (landingPageMetrics.length === 0) {
              return (
                <EmptyTabState
                  icon={Globe}
                  title="No Landing Page Data"
                  description="Landing-page performance appears here once ads have a final URL set and have generated impressions."
                />
              );
            }

            // #5997 — roll a URL's ad groups' post-click-quality buckets into one
            // distribution. It's a lossy rollup of a keyword-level, query-contextual
            // signal, so we surface the worst present bucket (most actionable) AND the
            // spread rather than collapsing to a single verdict.
            const rollupExperience = (adGroupIds: Set<string>) => {
              let above = 0;
              let average = 0;
              let below = 0;
              for (const agId of adGroupIds) {
                const b = landingPageExperienceByAdGroup.get(agId);
                if (b) {
                  above += b.above;
                  average += b.average;
                  below += b.below;
                }
              }
              return { above, average, below, total: above + average + below };
            };

            const landingPageColumns: ColumnDef<LandingPageRow, unknown>[] = [
              { accessorKey: 'url', header: 'Landing page', cell: ({ row }) => {
                // Truncate for display but keep the full URL in the link.
                const url = row.original.url;
                const display = url.length > 60 ? url.slice(0, 60) + '…' : url;
                const campaigns = Array.from(row.original.campaigns);
                const campaignLabel = campaigns.length === 1
                  ? campaigns[0]
                  : `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`;
                return (
                  <div>
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-sm font-medium text-[#290BD9] hover:text-[#2309C5] transition-colors"
                      title={url}
                    >
                      {display}
                    </a>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {row.original.adCount} ad{row.original.adCount === 1 ? '' : 's'} · {campaignLabel}
                    </div>
                  </div>
                );
              }},
              { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
              { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
              { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
              { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${row.original.ctr.toFixed(2)}%` },
              { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
              { accessorKey: 'cpa', header: 'CPA', cell: ({ row }) => fmt(row.original.cpa, cs) },
              {
                id: 'lpe',
                header: 'Landing page exp.',
                cell: ({ row }) => {
                  const { above, average, below, total } = rollupExperience(row.original.adGroupIds);
                  if (total === 0) return <span className="text-gray-400">—</span>;
                  const worst = below > 0 ? 'below' : average > 0 ? 'average' : 'above';
                  const cls =
                    worst === 'below'
                      ? 'bg-red-100 text-red-700'
                      : worst === 'average'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-green-100 text-green-700';
                  const label = worst === 'below' ? 'Below avg' : worst === 'average' ? 'Average' : 'Above avg';
                  const spread = [
                    above ? `${above} above` : null,
                    average ? `${average} avg` : null,
                    below ? `${below} below` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <div className="flex items-center gap-1.5">
                      <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium', cls)}>
                        {label}
                      </span>
                      <span className="whitespace-nowrap text-[10px] text-gray-400">{spread}</span>
                    </div>
                  );
                },
              },
            ];

            // #4302 — CSV export for Landing Pages.
            const landingPageExportColumns: ExportColumn<LandingPageRow>[] = [
              { header: 'Landing page', value: r => r.url },
              { header: 'Ads', value: r => r.adCount },
              { header: 'Campaigns', value: r => Array.from(r.campaigns).join(' | ') },
              { header: 'Impressions', value: r => r.impressions },
              { header: 'Clicks', value: r => r.clicks },
              { header: 'CTR', value: r => `${r.ctr.toFixed(2)}%` },
              { header: 'Conversions', value: r => r.conversions },
              { header: 'Cost', value: r => r.cost },
              { header: 'CPA', value: r => r.cpa },
              {
                header: 'Landing page exp.',
                value: r => {
                  const { above, average, below, total } = rollupExperience(r.adGroupIds);
                  if (total === 0) return '';
                  const worst = below > 0 ? 'Below avg' : average > 0 ? 'Average' : 'Above avg';
                  return `${worst} (${above} above · ${average} avg · ${below} below)`;
                },
              },
            ];

            return (
              <div className="space-y-2">
                <div className="flex items-center justify-end pt-1 pr-10">
                  <ExportCsvButton
                    rows={landingPageMetrics}
                    columns={landingPageExportColumns}
                    title="Landing pages report"
                    dateRange={dateRangeLabel}
                    startDate={dateRangeFilter.start_date}
                    endDate={dateRangeFilter.end_date}
                    filenameSlug="landing-pages-report"
                  />
                </div>
                <DataTable<LandingPageRow>
                  data={landingPageMetrics}
                  columns={landingPageColumns}
                  pageSize={15}
                  variant="bordered"
                  emptyState="No landing-page data available for this period"
                />
              </div>
            );
          }

          default:
            return null;
        }
      }}
    </WidgetGrid>
  );
}
